import { html, nothing, type TemplateResult } from 'lit-html';
import { PkOverlayBase } from '../_internal/pk-overlay-base.js';
import styles from './pk-side-sheet.css?inline';

/**
 * `<pk-side-sheet>` — slide-over panel anchored to the right edge of the viewport. v0.6
 *
 * Attributes:
 *   `open`            — boolean; shows/hides the sheet.
 *   `title`           — panel heading text (required).
 *   `width`           — enum: `narrow` (360px) | `medium` (480px, default) | `wide` (640px).
 *   `dismissible`     — enum: `none` | `escape` | `backdrop` (default; covers escape + backdrop click).
 *   `return-focus-to` — id of element to focus on close. If absent, returns to previously-active element.
 *
 * Slots:
 *   `body`      — main content / form.
 *   `primary`   — primary action button.
 *   `secondary` — cancel/dismiss button.
 *   `header`    — optional eyebrow above the title.
 *   `footer`    — optional status row below the primary/secondary actions.
 *
 * Events:
 *   `pk-open`   — fires when the sheet becomes visible (no detail).
 *   `pk-cancel` — fires when Escape, backdrop, or close-X dismisses the sheet (no detail).
 *   `pk-close`  — fires after pk-cancel. detail: { reason: 'cancel' }.
 *
 * Consumer-fired event (documented here, NOT fired by pk-side-sheet itself):
 *   `pk-confirm` — consumer fires from the primary slot's button on form submit.
 *
 * ARIA:
 *   `role="dialog"` `aria-modal="true"` on the panel element.
 *   `aria-labelledby` referencing the title heading.
 *   Focus trap active while open.
 *   `inert` applied to background siblings while open.
 *
 * Animation:
 *   Desktop (>720px): slides in from the right (translateX 100% → 0).
 *   Mobile  (≤720px): full-width, slides up from the bottom (matches pk-modal bottom-sheet pattern).
 */
export class PkSideSheet extends PkOverlayBase {
    static readonly tagName = 'pk-side-sheet';

    static get observedAttributes(): string[] {
        return ['open', 'title', 'width', 'dismissible', 'return-focus-to'];
    }

    protected get _idPrefix(): string {
        return 'pk-side-sheet';
    }

    // ── Template ─────────────────────────────────────────────────────────────

    protected override template(): TemplateResult {
        const isOpen    = this.hasAttribute('open');
        const title     = this.getAttribute('title') ?? '';
        const width     = this.getAttribute('width') ?? 'medium';
        const showClose = this._isDismissible();

        if (!isOpen) {
            return html`<style>${styles}</style>`;
        }

        return html`
            <style>${styles}</style>
            <div class="backdrop"
                role="presentation"
                @click=${(e: MouseEvent) => this._onBackdropClick(e)}>
                <div class="panel"
                    role="dialog"
                    aria-modal="true"
                    aria-label=${title || 'Side panel'}
                    aria-labelledby=${this._titleId}
                    data-width=${width}
                    @keydown=${(e: KeyboardEvent) => this._handleTabKey(e)}>

                    <div class="panel-header">
                        <slot name="header"></slot>
                        <div class="panel-header-row">
                            <h2 class="panel-title" id=${this._titleId}>${title}</h2>
                            ${showClose ? html`
                                <button class="close-btn"
                                    type="button"
                                    aria-label="Close panel"
                                    @click=${() => this._close()}>
                                    &#x2715;
                                </button>
                            ` : nothing}
                        </div>
                    </div>

                    <div class="panel-body">
                        <slot name="body"></slot>
                    </div>

                    <div class="panel-footer">
                        <div class="panel-actions">
                            <slot name="secondary"></slot>
                            <slot name="primary"></slot>
                        </div>
                        <slot name="footer"></slot>
                    </div>
                </div>
            </div>
        `;
    }
}

if (!customElements.get(PkSideSheet.tagName)) {
    customElements.define(PkSideSheet.tagName, PkSideSheet);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-side-sheet': PkSideSheet;
    }
}
